/**
 * R-16 T-135 — Edit quick links: a kit Modal listing the reader's links with a drag handle AND up/down buttons (both, for
 * keyboard users), a remove button each, and Reset to defaults. Save writes the list once; reset saves "the defaults".
 */
import { useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, X } from 'lucide-react';
import { Button, Modal } from '@/kit';
import type { MenuItemDecl } from '@/features/apps/apps-api';
import { moveId } from './model';

export function QuickLinksEditor({ items, defaults, saving, onSave, onClose }: { items: MenuItemDecl[]; defaults: MenuItemDecl[]; saving: boolean; onSave: (ids: string[] | null, reset: boolean) => Promise<unknown>; onClose: () => void }) {
  const labels = new Map([...items, ...defaults].map((it) => [it.id, it.label] as const));
  const start = items.map((it) => it.id);
  const [ids, setIds] = useState<string[]>(start);
  const [reset, setReset] = useState(false);
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dirty = reset || ids.join('\n') !== start.join('\n');
  const save = async () => {
    setError(null);
    try {
      await onSave(reset && ids.join('\n') === defaults.map((d) => d.id).join('\n') ? null : ids, reset);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Your quick links were not saved. Try again.');
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      size="medium"
      title="Edit quick links"
      dirty={dirty}
      data-testid="quick-links-editor"
      footerNote={error ?? `${ids.length} ${ids.length === 1 ? 'link' : 'links'}; the first ten show in the row`}
      footer={(close) => (
        <>
          <Button
            variant="quiet"
            data-testid="ql-reset"
            onClick={() => {
              setIds(defaults.map((d) => d.id));
              setReset(true);
            }}
          >
            Reset to defaults
          </Button>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} data-testid="ql-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    >
      {ids.length === 0 ? <p className="k-meta">No quick links. Star a page in any menu to add it, or reset to the defaults.</p> : null}
      <ul className="k-ql-edit" aria-label="Your quick links, in order">
        {ids.map((id, i) => {
          const label = labels.get(id) ?? id;
          return (
            <li
              key={id}
              data-testid={`ql-edit-${id}`}
              draggable
              data-dragging={drag === i || undefined}
              data-over={over === i && drag !== i ? true : undefined}
              onDragStart={(e) => {
                setDrag(i);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(i);
              }}
              onDragEnd={() => {
                setDrag(null);
                setOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (drag !== null) setIds((x) => moveId(x, drag, i));
                setDrag(null);
                setOver(null);
              }}
            >
              <span className="k-ql-grip" aria-hidden title="Drag to reorder">
                <GripVertical />
              </span>
              <span className="k-ql-name">{label}</span>
              {i > 0 ? (
                <Button icon aria-label={`Move ${label} up`} onClick={() => setIds((x) => moveId(x, i, i - 1))}>
                  <ArrowUp aria-hidden />
                </Button>
              ) : null}
              {i < ids.length - 1 ? (
                <Button icon aria-label={`Move ${label} down`} onClick={() => setIds((x) => moveId(x, i, i + 1))}>
                  <ArrowDown aria-hidden />
                </Button>
              ) : null}
              <Button icon aria-label={`Remove ${label}`} onClick={() => setIds((x) => x.filter((y) => y !== id))}>
                <X aria-hidden />
              </Button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
